import React from 'react';
import { Link } from 'react-router-dom';
import { LifeBuoy, GraduationCap, HelpCircle, Shield, FileText, DollarSign, ChevronRight } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';

/**
 * HELP AND LEGAL — the pages a creator needs occasionally, given a deliberate home.
 *
 * Owner 2026-08-23: "some links can be inside Profile -> Settings". They used to be four unlabelled
 * icons in the 272px sidebar rail, where they cost permanent attention to serve a rare need. Here they
 * are named rows in the page's own card idiom, so they read as a list you can scan rather than a
 * puzzle you decode.
 */
interface Row { to: string; label: string; hint: string; Icon: React.FC<{ className?: string }>; external?: boolean }

const ROWS: Row[] = [
  { to: '/tutorials', label: 'Tutorials', hint: 'Walkthroughs for the studio', Icon: GraduationCap, external: true },
  { to: '/faq', label: 'FAQ', hint: 'Common questions answered', Icon: HelpCircle, external: true },
  { to: '/privacy', label: 'Privacy', hint: 'How we handle your data', Icon: Shield, external: true },
  { to: '/terms', label: 'Terms', hint: 'The terms of service', Icon: FileText, external: true },
];

/** The admin cost console — only ever offered to an admin, never advertised to a creator. */
const ADMIN_ROW: Row = { to: '/admin', label: 'Cost management', hint: 'Platform spend and errors', Icon: DollarSign };

const rowClass =
  'flex items-center gap-3 rounded-lg px-2 py-2.5 -mx-2 hover:bg-secondary-50 dark:hover:bg-secondary-700/50 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-400 transition-colors';

const LinkRow: React.FC<{ row: Row }> = ({ row }) => (
  <Link
    to={row.to}
    {...(row.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    className={rowClass}
  >
    <row.Icon className="w-4 h-4 shrink-0 text-secondary-500 dark:text-secondary-400" />
    <span className="min-w-0 flex-1">
      <span className={`block ${TYPE.body} text-secondary-800 dark:text-secondary-100`}>{row.label}</span>
      <span className={`block ${TYPE.meta} text-secondary-400`}>{row.hint}</span>
    </span>
    <ChevronRight className="w-3.5 h-3.5 shrink-0 text-secondary-300 dark:text-secondary-600" />
  </Link>
);

const HelpLinksSection: React.FC<{ isAdmin?: boolean }> = ({ isAdmin }) => (
  <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6">
    <div className="flex items-center gap-2">
      <LifeBuoy className="w-5 h-5 text-secondary-700 dark:text-secondary-200" />
      <h3 className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>Help and legal</h3>
    </div>
    <div className="mt-3 flex flex-col">
      {ROWS.map((row) => <LinkRow key={row.to} row={row} />)}
      {isAdmin && <LinkRow row={ADMIN_ROW} />}
    </div>
  </div>
);

export default HelpLinksSection;
